import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SellerDashboardLayout from '../../components/SellerDashboardLayout/SellerDashboardLayout';
import { API_BASE_URL } from '../../config/api';
import './MyInquiriesPage.css';

const MyInquiriesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchInquiries = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}/messages/inquiries/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Failed to fetch inquiries');
      const data = await resp.json();
      setInquiries(data.inquiries || []);
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return formatDate(d);
  };

  const handleViewConversation = (inquiry) => {
    navigate(`/messages?chatId=${inquiry.id}`);
  };

  return (
    <SellerDashboardLayout>
      <div className="my-inquiries-page">
        <div className="miq-header">
          <h1>My Inquiries</h1>
          <p className="miq-subtitle">Track your product inquiries and supplier responses</p>
        </div>

        {loading ? (
          <div className="miq-loading">
            <div className="miq-spinner" />
            <p>Loading inquiries...</p>
          </div>
        ) : error ? (
          <div className="miq-error">
            <p>{error}</p>
            <button onClick={fetchInquiries}>Retry</button>
          </div>
        ) : inquiries.length === 0 ? (
          <div className="miq-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 6L12 13L2 6" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>No inquiries yet</h3>
            <p>Browse products and click "Inquire Now" to send inquiries to suppliers</p>
            <button className="miq-browse-btn" onClick={() => navigate('/')}>Browse Products</button>
          </div>
        ) : (
          <div className="miq-list">
            {inquiries.map(inq => (
              <div key={inq.id} className={`miq-card ${!inq.isRead ? 'unread' : ''}`}>
                <div className="miq-card-header" onClick={() => setExpandedId(expandedId === inq.id ? null : inq.id)}>
                  <div className="miq-card-left">
                    <div className="miq-avatar">
                      {inq.otherUser?.avatar ? (
                        <img src={inq.otherUser.avatar} alt="" />
                      ) : (
                        <span>{(inq.otherUser?.name || '?')[0].toUpperCase()}</span>
                      )}
                    </div>
                    <div className="miq-card-info">
                      <h3>{inq.otherUser?.name || 'Unknown'}</h3>
                      <span className="miq-role-badge">{inq.role === 'buyer' ? 'You → Supplier' : 'Supplier → You'}</span>
                    </div>
                  </div>
                  <div className="miq-card-right">
                    <span className="miq-time">{formatTime(inq.updatedAt)}</span>
                    <span className={`miq-status ${inq.messageCount > 1 ? 'replied' : 'pending'}`}>
                      {inq.messageCount > 1 ? 'Replied' : 'Pending'}
                    </span>
                    {!inq.isRead && <span className="miq-unread-dot" />}
                    <svg className={`miq-chevron ${expandedId === inq.id ? 'open' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>

                {expandedId === inq.id && (
                  <div className="miq-card-body">
                    {inq.inquiryItems && inq.inquiryItems.length > 0 && (
                      <div className="miq-products">
                        <h4>Products Inquired ({inq.inquiryItems.length})</h4>
                        <div className="miq-product-list">
                          {inq.inquiryItems.map((item, idx) => (
                            <div key={idx} className="miq-product-item">
                              {item.image && <img src={item.image} alt="" className="miq-product-img" />}
                              <div className="miq-product-info">
                                <span className="miq-product-name">{item.name || item.description}</span>
                                {item.price && <span className="miq-product-price">{item.price}</span>}
                                {item.moq && <span className="miq-product-moq">MOQ: {item.moq}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {inq.lastMessage && (
                      <div className="miq-last-message">
                        <h4>Last Message</h4>
                        <p>{inq.lastMessage.content}</p>
                        <span className="miq-msg-meta">{inq.lastMessage.sender} · {formatTime(inq.lastMessage.time)}</span>
                      </div>
                    )}

                    <div className="miq-card-actions">
                      <button className="miq-view-btn" onClick={() => handleViewConversation(inq)}>
                        View Conversation
                      </button>
                      <span className="miq-msg-count">{inq.messageCount} message{inq.messageCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </SellerDashboardLayout>
  );
};

export default MyInquiriesPage;
